import React, {useState, useEffect, useCallback} from "react";
import {Table, Spin} from "antd";
import {CalendarOutlined, UpOutlined, DownOutlined, ShopOutlined} from "@ant-design/icons";
import moment from "moment";
import {DefaultTransactionTable} from "../../components/DefaultTransactionTable";
import {
    CourierTableWrapper,
    DaysContainer,
    DayWrapper,
    DayRow,
    DayIcon,
    DayInfo,
    DayTotal,
    ExpandedDayContent,
    DayTransactionTableWrapper,
    SubCompanyBox,
    SubCompanyHeader,
    SubCompanyName,
    SubCompanyTotal,
    SubCompanyContent
} from "../styles/CourierDetail.styles";
import {
    DETAIL_TABLE_SUB_TYPE_LABELS,
    normalizeTableData,
    providerCompanies
} from "constants/constants";

const getProviderCompanyLabel = value =>
    providerCompanies.find(o => o.value === value)?.name || value || "-";

const formatCurrency = amount => {
    if (amount === undefined || amount === null) return "-";
    if (amount === 0) return "0";
    return `${amount.toLocaleString("tr-TR")} TL`;
};

const SubCompanySection = ({subCompany, isExpanded, onToggle}) => {
    const displayName = subCompany?.subCompanyName || "-";

    return (
        <SubCompanyBox>
            <SubCompanyHeader onClick={onToggle}>
                <SubCompanyName>
                    <ShopOutlined style={{marginRight: 6}} />
                    {displayName}
                </SubCompanyName>
                <SubCompanyTotal>
                    <span className="amount">{formatCurrency(subCompany?.totalCourierAmount)}</span>
                    <span className="arrow">{isExpanded ? <UpOutlined /> : <DownOutlined />}</span>
                </SubCompanyTotal>
            </SubCompanyHeader>
            {isExpanded && (
                <SubCompanyContent>
                    <DefaultTransactionTable
                        transactions={subCompany.transactions}
                        subTypeLabels={DETAIL_TABLE_SUB_TYPE_LABELS}
                        Wrapper={DayTransactionTableWrapper}
                    />
                </SubCompanyContent>
            )}
        </SubCompanyBox>
    );
};

export const CourierDetailTable = ({
    data,
    sharedLogFilters,
    fetchDetail,
    pageParam,
    setPageParam
}) => {
    const [expandedCouriers, setExpandedCouriers] = useState([]);
    const [loadingCourierIds, setLoadingCourierIds] = useState([]);
    const [expandedDays, setExpandedDays] = useState({});
    const [expandedSubCompanies, setExpandedSubCompanies] = useState({});
    const [detailByCourierId, setDetailByCourierId] = useState({});
    const {rows, dataCount} = normalizeTableData(data);

    const startDate = sharedLogFilters?.startDate;
    const endDate = sharedLogFilters?.endDate;
    const paymentPeriodId = sharedLogFilters?.paymentPeriodId;
    const hasDateRange = Boolean(startDate && endDate);
    const hasPaymentPeriod = paymentPeriodId != null && paymentPeriodId !== "";

    useEffect(() => {
        setDetailByCourierId({});
        setExpandedCouriers([]);
        setLoadingCourierIds([]);
        setExpandedDays({});
        setExpandedSubCompanies({});
    }, [JSON.stringify(sharedLogFilters)]);

    const loadDetail = useCallback(
        courierId => {
            if (
                !fetchDetail ||
                (!hasDateRange && !hasPaymentPeriod) ||
                detailByCourierId[courierId]
            )
                return;
            setDetailByCourierId(prev => ({
                ...prev,
                [courierId]: {loading: true, data: null, error: null}
            }));
            fetchDetail({
                ...sharedLogFilters,
                groupType: "courier",
                courierId
            })
                .then(res => {
                    const detailData = res?.data != null ? res.data : res;
                    setDetailByCourierId(prev => ({
                        ...prev,
                        [courierId]: {
                            loading: false,
                            data: detailData,
                            error: null
                        }
                    }));
                })
                .catch(err => {
                    setDetailByCourierId(prev => ({
                        ...prev,
                        [courierId]: {
                            loading: false,
                            data: null,
                            error: err?.message || "Detay yüklenemedi"
                        }
                    }));
                });
        },
        [fetchDetail, sharedLogFilters, hasDateRange, hasPaymentPeriod, detailByCourierId]
    );

    const handleCourierExpand = (expanded, record) => {
        const courierId = record.courierId;
        if (!expanded) {
            setExpandedCouriers(prev => prev.filter(id => id !== courierId));
            return;
        }
        const detail = detailByCourierId[courierId];
        if (detail && !detail.loading) {
            setExpandedCouriers(prev => [...prev, courierId]);
        } else {
            setLoadingCourierIds(prev => [...prev, courierId]);
            loadDetail(courierId);
        }
    };

    useEffect(() => {
        if (loadingCourierIds.length === 0) return;
        const stillLoading = [];
        const toExpand = [];
        loadingCourierIds.forEach(courierId => {
            const detail = detailByCourierId[courierId];
            if (detail && !detail.loading) {
                toExpand.push(courierId);
            } else {
                stillLoading.push(courierId);
            }
        });
        if (toExpand.length > 0) {
            setLoadingCourierIds(stillLoading);
            setExpandedCouriers(prev => [...prev, ...toExpand]);
        }
    }, [detailByCourierId, loadingCourierIds]);

    const toggleDay = (courierId, dayIndex) => {
        const key = `${courierId}-${dayIndex}`;
        setExpandedDays(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleSubCompany = (courierId, dayIndex, subCompanyId) => {
        const key = `${courierId}-${dayIndex}-${subCompanyId}`;
        setExpandedSubCompanies(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const renderExpandedCourier = courier => {
        const courierId = courier.courierId;
        const detail = detailByCourierId[courierId];
        if (detail?.error) {
            return <div style={{padding: 24, color: "#e74c3c"}}>{detail.error}</div>;
        }
        const rawDays = Array.isArray(detail?.data)
            ? detail.data
            : detail?.data?.forecastDates || [];
        const sortedDays = [...rawDays].sort(
            (a, b) => new Date(b.forecastDate || b.date) - new Date(a.forecastDate || a.date)
        );

        if (sortedDays.length === 0) {
            return <div style={{padding: 24, color: "#666"}}>Bu dönem için detay bulunamadı.</div>;
        }

        return (
            <DaysContainer>
                {sortedDays.map((day, dayIndex) => {
                    const dayKey = `${courier.courierId}-${dayIndex}`;
                    const isExpanded = expandedDays[dayKey];
                    const dateStr = day.forecastDate || day.date;
                    const formattedDate = dateStr ? moment(dateStr).format("DD.MM.YYYY") : "-";
                    const dailyTotal =
                        day.totalCourierAmount ??
                        (day.transactions || []).reduce(
                            (sum, t) => sum + (t.courierAmount || 0),
                            0
                        );

                    return (
                        <DayWrapper key={dayIndex}>
                            <DayRow onClick={() => toggleDay(courier.courierId, dayIndex)}>
                                <DayIcon>
                                    <CalendarOutlined />
                                </DayIcon>
                                <DayInfo>{formattedDate}</DayInfo>
                                <DayTotal>
                                    <span className="label">Günlük Toplam Kazanç</span>
                                    <span className="amount">{formatCurrency(dailyTotal)}</span>
                                    <span className="arrow">
                                        {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                    </span>
                                </DayTotal>
                            </DayRow>
                            {isExpanded && (
                                <ExpandedDayContent>
                                    <h4>Kurye Toplam Bilgisi</h4>
                                    {(day.transactions?.length > 0 && (
                                        <DefaultTransactionTable
                                            transactions={day.transactions}
                                            subTypeLabels={DETAIL_TABLE_SUB_TYPE_LABELS}
                                            Wrapper={DayTransactionTableWrapper}
                                        />
                                    )) ||
                                        null}
                                    {day.subCompanies?.length > 0 ? <h4>Alt Firmalar</h4> : null}
                                    {day.subCompanies?.map(subCompany => {
                                        const scKey = `${courier.courierId}-${dayIndex}-${subCompany.subCompanyId}`;
                                        return (
                                            <SubCompanySection
                                                key={subCompany.subCompanyId}
                                                subCompany={subCompany}
                                                totalCourierAmount={day?.totalCourierAmount ?? 0}
                                                isExpanded={!!expandedSubCompanies[scKey]}
                                                onToggle={() =>
                                                    toggleSubCompany(
                                                        courier.courierId,
                                                        dayIndex,
                                                        subCompany.subCompanyId
                                                    )
                                                }
                                            />
                                        );
                                    })}
                                </ExpandedDayContent>
                            )}
                        </DayWrapper>
                    );
                })}
            </DaysContainer>
        );
    };

    return (
        <CourierTableWrapper>
            <Table
                dataSource={rows}
                rowKey="courierId"
                pagination={{
                    total: dataCount,
                    current: pageParam,
                    pageSize: 10,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    onChange: page => setPageParam(page)
                }}
                expandable={{
                    expandedRowRender: renderExpandedCourier,
                    expandedRowKeys: expandedCouriers,
                    onExpand: handleCourierExpand
                }}
            >
                <Table.Column
                    title="Kurye"
                    dataIndex="courierId"
                    render={(courierId, row) => (
                        <span style={{fontWeight: 500}}>
                            {row?.fullname}
                            {loadingCourierIds?.includes?.(courierId) && (
                                <Spin size="small" style={{marginLeft: 8}} />
                            )}
                        </span>
                    )}
                />
                <Table.Column
                    title="Toplam"
                    dataIndex="totalCourierAmount"
                    render={totalCourierAmount => (
                        <span style={{fontWeight: 500}}>{formatCurrency(totalCourierAmount)}</span>
                    )}
                />
                <Table.Column
                    title="Cüzdan"
                    dataIndex="isWalletActive"
                    render={val => (
                        <span
                            style={{
                                fontWeight: 500,
                                color: val === true ? "#007E00" : val === false ? "#e74c3c" : "#666"
                            }}
                        >
                            {val === true ? "Var" : val === false ? "Yok" : "-"}
                        </span>
                    )}
                />
                <Table.Column
                    title="Sağlayıcı Firma"
                    dataIndex="providerCompany"
                    render={providerCompany => (
                        <span style={{fontWeight: 500}}>
                            {getProviderCompanyLabel(providerCompany)}
                        </span>
                    )}
                />
            </Table>
        </CourierTableWrapper>
    );
};
